import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Badge, Spinner, Table, Form } from 'react-bootstrap';
import { useListEntrepreneurApplicationsQuery } from '../../app/api';
import { brand, cardStyle, headStyle, CLAUDE_TRACK, ClaudeTrackBadge, EntPage, FilterBar, filterInputStyle, claudeSignedUp } from './common';

/* Entrepreneur -> Claude -> Claude Firms: every firm's Claude account - the
 * application, domain, seats and payment method. Each row opens that firm's
 * Claude page (ClaudeFirmDetail).
 *
 * The hub side of a firm (its members and logins) is the separate Firms
 * directory. By default this list only shows firms that are actually on the
 * Claude track - applied, or with a seat requested or live - since most hub
 * firms never sign up and listing them here as "not signed up" is noise.
 */

const onClaudeTrack = (a) => claudeSignedUp(a) || (a.claude_seats || 0) > 0 || (a.pending_seats || 0) > 0;

// Claude-only "needs attention" hint from the list payload alone; the precise
// list is on the firm's Claude page.
function attentionFor(a) {
  if (a.application_status === 'SUBMITTED') return 'Review application';
  if (a.application_status === 'REJECTED' || a.application_status === 'SUSPENDED') return null;
  const reasons = [];
  if ((a.email_domains?.length || 0) > 0 && a.domain_status !== 'VERIFIED' && a.application_status !== 'DRAFT') reasons.push('Confirm domain');
  if (!a.payment_authorized && a.application_status !== 'DRAFT') reasons.push('No payment method');
  if ((a.pending_seats || 0) > 0) reasons.push(`${a.pending_seats} seat${a.pending_seats === 1 ? '' : 's'} requested`);
  return reasons.length ? reasons.join(' · ') : null;
}

const td = { padding: '0.55rem 0.9rem', verticalAlign: 'middle' };
const th = { padding: '0.6rem 0.9rem' };

export default function ClaudeFirms() {
  const navigate = useNavigate();
  const { data: firms = [], isLoading } = useListEntrepreneurApplicationsQuery();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showAll, setShowAll] = useState(false);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return firms.filter(a => {
      if (!showAll && !statusFilter && !onClaudeTrack(a)) return false;
      if (statusFilter && a.application_status !== statusFilter) return false;
      if (q && !`${a.firm_name || ''} ${(a.email_domains || []).join(' ')}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [firms, search, statusFilter, showAll]);

  const hiddenCount = firms.filter(a => !onClaudeTrack(a)).length;

  return (
    <EntPage title="Claude Firms" icon="stars"
      subtitle="Firms on the optional Claude add-on: applications, domains, seats and payment methods. A firm's members are managed under Firms.">
      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>Claude accounts</span>
          <FilterBar search={search} onSearchChange={setSearch} placeholder="Search firm or domain…">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} aria-label="Status" style={filterInputStyle}>
              <option value="">Any status</option>
              {Object.entries(CLAUDE_TRACK).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
            </select>
            <Form.Check type="switch" id="claude-show-all" checked={showAll} onChange={e => setShowAll(e.target.checked)}
              label={<span style={{ fontSize: '0.78rem', fontWeight: 500, color: brand.charcoal }}>Include firms not signed up ({hiddenCount})</span>} />
          </FilterBar>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : rows.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
              <i className="bi bi-stars" style={{ fontSize: '1.75rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
              {firms.some(onClaudeTrack) ? 'No firms match these filters.' : 'No firm has signed up for Claude yet.'}
            </div>
          ) : (
            <Table hover responsive className="mb-0" style={{ fontSize: '0.88rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={th}>Firm</th>
                <th style={th}>Status</th>
                <th style={th}>Domain</th>
                <th style={th}>Seats</th>
                <th style={th}>Payment method</th>
                <th style={th}>Needs attention</th>
                <th style={{ ...th, width: 30 }} />
              </tr></thead>
              <tbody>
                {rows.map(a => {
                  const attention = attentionFor(a);
                  const signed = claudeSignedUp(a);
                  return (
                    <tr key={a.firm_id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/entrepreneur/claude-firms/${a.firm_id}`)}>
                      <td style={td}>
                        <div style={{ fontWeight: 700, color: brand.deepBlue }}>{a.firm_name}</div>
                        {a.email_domains?.length > 0 && (
                          <div style={{ color: brand.charcoal, fontSize: '0.75rem' }}>{a.email_domains.join(', ')}</div>
                        )}
                      </td>
                      <td style={td}><ClaudeTrackBadge status={a.application_status} /></td>
                      <td style={td}>
                        {signed
                          ? <Badge bg={a.domain_status === 'VERIFIED' ? 'success' : 'warning'} text={a.domain_status === 'VERIFIED' ? undefined : 'dark'}>{a.domain_status}</Badge>
                          : <span style={{ color: brand.charcoal }}>-</span>}
                      </td>
                      <td style={{ ...td, color: brand.charcoal }}>
                        {a.claude_seats || 0} in Claude{a.pending_seats ? ` · ${a.pending_seats} requested` : ''}
                      </td>
                      <td style={{ ...td, color: brand.charcoal }}>{signed ? (a.payment_authorized ? 'On file' : 'None') : '-'}</td>
                      <td style={td}>
                        {attention ? (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: brand.goldenrod, fontWeight: 600, fontSize: '0.78rem' }}>
                            <i className="bi bi-exclamation-circle-fill" />{attention}
                          </span>
                        ) : signed ? (
                          <span style={{ color: brand.green, fontSize: '0.78rem' }}><i className="bi bi-check-circle me-1" />All set</span>
                        ) : null}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}><i className="bi bi-chevron-right" style={{ color: brand.tealBlue }} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </EntPage>
  );
}
